export interface Cart {
  id: number;
  hasDiscount: boolean;
  savingAmount: number;
  priceWithTax: string;
  priceToPay: string;
  photoBundleDiscountPrice: string;
  applicablePlatformFee: number;
  formattedPlatformFee: string;
  priceToPayWithPlatformFee: string;
  applicableShippingFee: number;
  formattedShippingFee: string;
  freeShippingEarned: boolean;
  freeShippingSavedFormatted: string | null;
  freeShippingThreshold: string | null;
  freeShippingAmountNeeded: string | null;
  printRetailSubtotalFormatted: string;
  freeShippingProgressPercent: number;
  priceToPayWithAllFees: string;
  photoBundleDiscountAvailed: boolean;
  payableAmount: string;
  chargeable: boolean;
  appliedCouponCode: string | null;
  printVolumeDiscount: number;
  currency: string;
  sales: CartItemApiResponse[];
}

export interface CheckoutData {
  formAction: string;
  captureEmailCheckout: boolean;
  customerEmail: string;
  downloadRequiresSubscription: boolean;
  prepaidCartRequiresReview: boolean;
  csrfToken: string;
}

export interface CheckoutPageProps {
  mounted: boolean;
  cartId: number;
  checkout: CheckoutData;
  currency: string;
  currencySymbol: string;
  eventGalleryUrl: string | null;
  promoCodeEnabled: boolean;
}

export interface DiscountTier {
  minQuantity: number;
  kind: "percentage" | "price";
  value: number;
}

export interface PrintProduct {
  id: number;
  eventPrintProductId: number;
  title: string;
  totalPrice: number;
  totalPriceFormatted: string;
  description: string;
  currency: string;
  sku: string;
  previewImageThumbnailURL: string | null;
  discountTiers: DiscountTier[];
  previewImageURL: string | null;
  freeShipping: boolean;
}

export interface Sale {
  id: number;
  productType: string;
  printProductId: number | null;
  printProductName: string | null;
  photoId: number;
  eventId: number;
  quantity: number;
  totalPrice: number;
  unitPriceFormatted: string;
  totalPriceFormatted: string;
  originalTotalFormatted: string;
  hasLineDiscount: boolean;
  digitalDiscountedTotalFormatted?: string | null;
  currency: string;
  purchased: boolean;
  digitalDownloadDisabled?: boolean;
}

export interface Photo {
  id: number;
  thumbnailUrl: string;
  videoAsset: boolean;
  mediaType: string;
  filename?: string;
}

export interface PhotoCartGroup {
  photo: Photo;
  sales: Sale[];
}

// Raw API response format for cart items
export interface CartItemApiResponse {
  id: number;
  productType: string;
  printProductId: number | null;
  printProductName: string | null;
  photoId: number;
  eventId?: number;
  quantity?: number;
  totalPrice?: string;
  unitPriceFormatted?: string;
  totalPriceFormatted?: string;
  originalTotalFormatted?: string;
  hasLineDiscount?: boolean;
  digitalDiscountedTotalFormatted?: string | null;
  currency: string;
  purchased: boolean;
  digitalDownloadDisabled?: boolean;
  photo?: {
    id: number;
    thumbnailUrl: string;
    videoAsset: boolean;
    mediaType: string;
  };
}

// API response wrapper for cart items
export interface CartApiResponse {
  cart: Cart;
}

// Interface for cart item update operations
export interface CartUpdateItem {
  id?: number;
  product_type?: string;
  photo_id?: number;
  print_product_id?: number;
  event_print_product_id?: number;
  quantity?: number;
  _destroy?: boolean;
}

export interface CheckoutPageState {
  checked: boolean;
  loading: boolean;
  cart: Cart | null;
  printProducts: PrintProduct[];
  photoCartGroups: PhotoCartGroup[];
  showFreeShippingPopup: boolean;
  showFreeShippingUnlockedPopup: boolean;
}

export const isDigitalDownload = (productType: string): boolean => {
  return (
    productType === "digital_photo_download" ||
    productType === "digital_video_download"
  );
};
